/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Thin wrapper around the browser Web Speech API for voice-follow. EXPERIMENTAL.
 * Requires Chrome-family browser + a secure context (localhost or HTTPS). Emits
 * the recent transcript tail; the caller matches it against the script.
 */

export function isSpeechSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
  );
}

export interface VoiceFollowController {
  start(): void;
  stop(): void;
}

export function createVoiceFollow(
  lang: string,
  onTranscript: (recentText: string) => void,
  onStatus: (status: string) => void,
): VoiceFollowController | null {
  const Ctor: any = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  if (!Ctor) return null;

  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = lang;
  let running = false;

  rec.onresult = (e: any) => {
    // Build the FULL transcript (all finalised results + current interim) so the
    // matcher always has the words leading up to the latest one — essential for
    // disambiguating repeated words like "conversation".
    let text = '';
    for (let i = 0; i < e.results.length; i += 1) {
      text += `${e.results[i][0].transcript} `;
    }
    onTranscript(text);
  };
  rec.onerror = (e: any) => onStatus(`error: ${e.error ?? 'unknown'}`);
  rec.onend = () => {
    // Chrome ends recognition periodically; restart while we're meant to be running.
    if (running) {
      try {
        rec.start();
      } catch {
        /* start() throws if already started — ignore */
      }
    } else {
      onStatus('stopped');
    }
  };

  return {
    start() {
      running = true;
      onStatus('listening…');
      try {
        rec.start();
      } catch {
        /* ignore */
      }
    },
    stop() {
      running = false;
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    },
  };
}

export interface Transcriber {
  start(): void;
  stop(): void;
}

/**
 * A standalone transcriber for the "living log" recording. Emits only FINALISED
 * speech segments (clean lines, no interim churn). Separate from voice-follow so a
 * show can be recorded without driving the scroll.
 */
export function createTranscriber(lang: string, onLine: (text: string) => void): Transcriber | null {
  const Ctor: any = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  if (!Ctor) return null;

  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = false;
  rec.lang = lang;
  let running = false;

  rec.onresult = (e: any) => {
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      const r = e.results[i];
      if (r.isFinal) {
        const t = String(r[0].transcript).trim();
        if (t) onLine(t);
      }
    }
  };
  rec.onerror = () => {
    /* transient; onend restarts while running */
  };
  rec.onend = () => {
    if (running) {
      try {
        rec.start();
      } catch {
        /* already started — ignore */
      }
    }
  };

  return {
    start() {
      running = true;
      try {
        rec.start();
      } catch {
        /* ignore */
      }
    },
    stop() {
      running = false;
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    },
  };
}
